import React, { useEffect, useState, useRef, useCallback } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  FormControl,
  FormControlLabel,
  Select,
  MenuItem,
  Checkbox,
  Radio,
  RadioGroup,
  Tooltip,
  Grid,
  Box,
  Typography,
  Button,
  IconButton,
  Menu,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import PowerSharpIcon from "@mui/icons-material/PowerSharp";
import FilterListIcon from "@mui/icons-material/FilterList";
import BackdropComponent from "../../Spinner/Spinner";
import SnackBar from "components/SnackBar/SnackBar";
import LastUpdateTooltip from "../Helpers/LastUpdateTooltip";
import { bulkEditStoreAction } from "../../../Store/BulkEdit";
import { SnackBarStoreAction } from "Store/SnackBar";
import {
  getStatusDisposition,
  postStatusDispositionV2,
  getreasonDetails,
} from "../../../services/userServices";
import applicationUsageLog from "../../../utils/applicationUsagesLog";
import PropTypes from "prop-types";
import classes from "./StatusDisposition.module.css";

// Custom Styled Buttons
const CustomButton = styled(Button)(() => ({
  fontFamily: "Urbanist, Verdana, Arial, sans-serif",
  fontSize: "12px",
  letterSpacing: "0.10em",
  borderRadius: "8px",
  fontWeight: "600",
  textTransform: "none",
  color: "white",
  backgroundColor: "#3299FF",
  padding: "8px 20px",
  "&:hover": {
    backgroundColor: "#2878CC",
  },
}));

const CustomOutlinedButton = styled(Button)(() => ({
  fontFamily: "Urbanist, Verdana, Arial, sans-serif",
  fontSize: "12px",
  letterSpacing: "0.10em",
  borderRadius: "8px",
  fontWeight: "600",
  textTransform: "none",
  borderColor: "#3299FF",
  color: "#3299FF",
  padding: "8px 20px",
  "&:hover": {
    borderColor: "#3299FF",
    backgroundColor: "#E0F2FF",
  },
}));

// Utility functions
const findOrtStatus = (status) => {
  if (status === "Pass") return "true";
  else if (status === "Fail") return "false";
  else return "null";
};

const prepareDropdownValues = (sourceData, key) => {
  return [
    ...new Set(
      ["ALL"].concat([...new Set(sourceData.map((item) => item[key]))]),
    ),
  ].filter(Boolean);
};

const getTraineeProductionStatus = (productionStatus) =>
  productionStatus === null ? "No - Unknown" : productionStatus;

const getFTRosterReadyTimestampStatus = (status) =>
  status === true ? "True" : "False";

const StatusDisposition = (props) => {
  const { t } = useTranslation(["statusDisposition"]);
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const initialRender = useRef(true);

  // Redux state
  const classTraineeId = useSelector((state) => state.bulkEdit);
  const { classRequestId, classRosterId } = classTraineeId;

  // Component state
  const [loading, setLoading] = useState(false);
  const [statusDispositionData, setStatusDispositionData] = useState([]);
  const [statusDispositionDataFiltered, setStatusDispositionDataFiltered] =
    useState([]);
  const [globalData, setGlobalData] = useState([]);
  const [reasonData, setReasonData] = useState([]);
  const [order, setOrder] = useState("ASC");

  // Snackbar state
  const [snack, setSnack] = useState({
    open: false,
    message: "",
    severity: "",
  });

  // Filter menu anchor states
  const [filterMenuAnchors, setFilterMenuAnchors] = useState({
    traineeStatus: null,
    ert: null,
    acc: null,
    tci: null,
    tciRecovery: null,
    ttd: null,
    ttdRecovery: null,
    ttf: null,
    productTraining: null,
    nesting: null,
    ort: null,
    ftrr: null,
    endorsedNesting: null,
    production: null,
  });

  // Header bulk edit states
  const [traineeStatusBottomDropdown, setTraineeStatusBottomDropdown] =
    useState("");
  const [ftrr, setFtrr] = useState(false);
  const [endorsedToNesting, setEndorsedToNesting] = useState(false);
  const [ttf, setTtf] = useState("");
  const [productTraining, setProductTraining] = useState("");
  const [nesting, setNesting] = useState("");
  const [initialProductionValue, setInitialProductionValue] = useState("");

  // Dropdown values for filtering (arrays of options)
  const [traineeStatusDropDown, setTraineeStatusDropDown] = useState(["ALL"]);
  const [ertValues, setErtValues] = useState(["ALL"]);
  const [accValues, setAccValues] = useState(["ALL"]);
  const [tciValues, setTciValues] = useState(["ALL"]);
  const [tciRecoveryValues, setTciRecoveryValues] = useState(["ALL"]);
  const [ttdValues, setTtdValues] = useState(["ALL"]);
  const [ttdRecoveryValues, setTtdRecoveryValues] = useState(["ALL"]);
  const [ttfValues, setTtfValues] = useState(["ALL"]);
  const [productTrainingValues, setProductTrainingValues] = useState(["ALL"]);
  const [nestingValues, setNestingValues] = useState(["ALL"]);
  const [ortValues, setORTValues] = useState(["ALL"]);
  const [ftrrValues, setFtrrValues] = useState(["ALL"]);
  const [endorsedNestingValues, setEndorsedNestingValues] = useState(["ALL"]);
  const [productionValues, setProductionValues] = useState(["ALL"]);

  // Filter dropdown selected values (track active filters for visual indicators)
  const [ertDropDownValue, setErtDropDownValue] = useState("ALL");
  const [accDropDownValue, setAccDropDownValue] = useState("ALL");
  const [tciDropDownValue, setTciDropDownValue] = useState("ALL");
  const [tciRecoveryDropDownValue, setTciRecoveryDropDownValue] =
    useState("ALL");
  const [ttdDropDownValue, setTtdDropDownValue] = useState("ALL");
  const [ttdRecoveryDropDownValue, setTtdRecoveryDropDownValue] =
    useState("ALL");
  const [ttfDropDownValue, setTtfDropDownValue] = useState("ALL");
  const [productTrainingDropDownValue, setProductTrainingDropDownValue] =
    useState("ALL");
  const [nestingDropDownValue, setNestingDropDownValue] = useState("ALL");
  const [ortDropDownValue, setORTDropDownValue] = useState("ALL");
  const [ftrrDropDownValue, setFTRRDropDownValue] = useState("ALL");
  const [endorsedNestingDropDownValue, setEndorsedNestingDropDownValue] =
    useState("ALL");
  const [productionDropDownValue, setProductionDropDownValue] = useState("ALL");

  // Dropdown option lists
  const TTFDropDown = useCallback(() => {
    return [
      "Active",
      "Pending Term",
      "Term",
      "Reschedule",
      "Transfer",
      "Unknown",
    ];
  }, []);

  const productionDropDown = useCallback(() => {
    return [
      "Yes - Active",
      "No - Unknown",
      "No - Holdover",
      "No - Pending Term",
      "No - Term",
      "No - Reschedule",
      "No - Transfer",
    ];
  }, []);

  const traineeStatusList = useCallback(() => {
    return [
      { id: 1, traineeStatus: "Active" },
      { id: 2, traineeStatus: "Terminated" },
      { id: 5, traineeStatus: "Suspended" },
      { id: 6, traineeStatus: "Revert" },
      { id: 7, traineeStatus: "Dormant" },
      { id: 8, traineeStatus: "Added In Error" },
      { id: 9, traineeStatus: "Pending R/T" },
      { id: 10, traineeStatus: "Pending TA" },
    ];
  }, []);

  // Filter menu handlers
  const handleFilterMenuOpen = (event, filterName) => {
    setFilterMenuAnchors({
      ...filterMenuAnchors,
      [filterName]: event.currentTarget,
    });
  };

  const handleFilterMenuClose = (filterName) => {
    setFilterMenuAnchors({
      ...filterMenuAnchors,
      [filterName]: null,
    });
  };

  const setupDropdowns = (responseData) => {
    setErtValues(prepareDropdownValues(responseData, "ERTStatus"));
    setAccValues(prepareDropdownValues(responseData, "ACCStatus"));
    setTciValues(prepareDropdownValues(responseData, "TCIDisposition"));
    setTciRecoveryValues(
      prepareDropdownValues(responseData, "TCIRecoveryDisposition"),
    );
    setTtdValues(prepareDropdownValues(responseData, "TechTestDisposition"));
    setTtdRecoveryValues(
      prepareDropdownValues(responseData, "TechTestRecoveryDisposition"),
    );
    setTtfValues(prepareDropdownValues(responseData, "TTFStatus"));
    setProductTrainingValues(
      prepareDropdownValues(responseData, "ProductTrainingStatus"),
    );
    setNestingValues(prepareDropdownValues(responseData, "NestingStatus"));
    setORTValues(prepareDropdownValues(responseData, "ORTStatus"));
    setFtrrValues(
      prepareDropdownValues(
        responseData,
        "FinalTrainingRosterReadyTimestampStatus",
      ),
    );
    setEndorsedNestingValues(
      prepareDropdownValues(responseData, "EndorsedToNestingStatus"),
    );
    setProductionValues(
      prepareDropdownValues(responseData, "ProductionStatus"),
    );
    setTraineeStatusDropDown(
      prepareDropdownValues(responseData, "TraineeStatus"),
    );
  };

  const getTraineeStatusReason = (trainseeStatusReason, reasonData) => {
    const filteredReason = reasonData.find(
      (e) => e.TraineeStatusReason === trainseeStatusReason,
    );
    if (trainseeStatusReason && filteredReason !== undefined)
      return filteredReason.TraineeStatusReasonId;
    else return reasonData[0]?.TraineeStatusReasonId;
  };

  const fetchData = async () => {
    setLoading(true);

    let obj = {
      classTraineeIdList: classTraineeId.traineeDetails,
    };

    const response = await getStatusDisposition(obj);

    if (response.message === "Success") {
      setupDropdowns(response.data);
      const reasonResponse = await getreasonDetails(6);
      setReasonData(reasonResponse.data);

      response.data = response.data.map((item) => {
        item.FinalTrainingRosterReadyTimestampStatus =
          item.FinalTrainingRosterReadyTimestampStatus === "True";
        item.EndorsedToNestingStatus = item.EndorsedToNestingStatus === true;
        item.ORTStatus = findOrtStatus(item.ORTStatus);
        item.TraineeStatusReason = getTraineeStatusReason(
          item.TraineeStatusReason,
          reasonResponse.data,
        );
        item.ProductionStatus = getTraineeProductionStatus(
          item.ProductionStatus,
        );
        return item;
      });

      setGlobalData(response.data);
      setStatusDispositionData(response.data);
      setStatusDispositionDataFiltered(response.data);
      dispatch(bulkEditStoreAction.setStatusDispositionsData(response.data));
      dispatch(bulkEditStoreAction.setStatusDispositionAllData(response.data));
      setLoading(false);
    } else {
      setSnack({
        open: true,
        message: "Failed to load data!",
        severity: "error",
      });
      setLoading(false);
    }

    if (response.data) {
      filtering(props.filterStatus, response.data);
    }
  };

  useEffect(() => {
    let cancelled = false;

    const initialize = async () => {
      try {
        await fetchData();

        if (!cancelled) {
          applicationUsageLog(
            localStorage.getItem("User"),
            localStorage.getItem("EmailId"),
            "ASTT",
            "Status Disposition",
          );
        }
      } catch (error) {
        if (!cancelled) {
          console.error("Failed to initialize:", error);
        }
      }
    };

    initialize();

    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;

    const handleReset = async () => {
      if (props.resetStatus === true) {
        try {
          await fetchData();

          if (!cancelled) {
            resetAllStates();
            props.onChange();
          }
        } catch (error) {
          if (!cancelled) {
            console.error("Failed to handle reset:", error);
          }
        }
      }
    };

    handleReset();

    return () => {
      cancelled = true;
    };
  }, [props.resetStatus]);

  useEffect(() => {
    if (initialRender.current) {
      initialRender.current = false;
    } else {
      setLoading(true);

      const timeout = setTimeout(() => {
        filtering(props.filterStatus, statusDispositionData);
        setLoading(false);
      }, 1000);

      return () => {
        clearTimeout(timeout);
      };
    }
  }, [props.filterStatus]);

  const resetAllStates = () => {
    setErtDropDownValue("ALL");
    setAccDropDownValue("ALL");
    setTciDropDownValue("ALL");
    setTciRecoveryDropDownValue("ALL");
    setTtdDropDownValue("ALL");
    setTtdRecoveryDropDownValue("ALL");
    setTtfDropDownValue("ALL");
    setProductTrainingDropDownValue("ALL");
    setNestingDropDownValue("ALL");
    setORTDropDownValue("ALL");
    setFTRRDropDownValue("ALL");
    setEndorsedNestingDropDownValue("ALL");
    setProductionDropDownValue("ALL");
    setTraineeStatusBottomDropdown("");
    setFtrr(false);
    setEndorsedToNesting(false);
    setTtf("");
    setProductTraining("");
    setNesting("");
    setInitialProductionValue("");
  };

  const filtering = (filter, data) => {
    if (filter === "ALL") {
      setStatusDispositionDataFiltered(data);
      props.closeLoader();
    } else {
      setStatusDispositionDataFiltered(
        data.filter((item) => item.TraineeStatus === props.filterStatus),
      );
      props.closeLoader();
    }
  };

  const snackReset = () => {
    setSnack({
      open: false,
      message: "",
      severity: "",
    });
  };

  const handleChange = (event, key, i, row) => {
    let data = statusDispositionData.map((item) => {
      if (
        item.OracleId === row.OracleId &&
        item.TraineeStatus === row.TraineeStatus
      ) {
        return {
          ...item,
          [key]: event.target.value === "" ? null : event.target.value,
        };
      } else {
        return item;
      }
    });
    setStatusDispositionData(data);
    dispatch(bulkEditStoreAction.setStatusDispositionsData(data));

    let trainee = statusDispositionDataFiltered.map((item) => {
      if (
        item.OracleId === row.OracleId &&
        item.TraineeStatus === row.TraineeStatus
      ) {
        return {
          ...item,
          [key]: event.target.value,
        };
      } else {
        return item;
      }
    });
    setStatusDispositionDataFiltered(trainee);
  };

  const handleChangeChecked = (event, row, i) => {
    let data = statusDispositionDataFiltered.map((item) => {
      if (
        item.OracleId === row.OracleId &&
        item.TraineeStatus === row.TraineeStatus
      ) {
        return {
          ...item,
          [event.target.name]:
            event.target.checked === true ? event.target.checked : false,
        };
      } else {
        return item;
      }
    });

    let trainee = statusDispositionData.map((item) => {
      if (
        item.OracleId === row.OracleId &&
        item.TraineeStatus === row.TraineeStatus
      ) {
        return {
          ...item,
          [event.target.name]:
            event.target.checked === true ? event.target.checked : false,
        };
      } else {
        return item;
      }
    });
    setStatusDispositionData(trainee);
    setStatusDispositionDataFiltered(data);
    dispatch(bulkEditStoreAction.setStatusDispositionsData(data));
  };

  const handleCheckChangeAll = (event, key) => {
    if (event.target.checked === true) {
      let data = statusDispositionData.map((item) => {
        return {
          ...item,
          [event.target.name]: event.target.checked,
        };
      });
      setStatusDispositionData(data);
      dispatch(bulkEditStoreAction.setStatusDispositionsData(data));

      let trainee = statusDispositionDataFiltered.map((item) => {
        return {
          ...item,
          [event.target.name]: event.target.checked,
        };
      });

      if (key === "FinalTrainingRosterReadyTimestampStatus") {
        setFtrr(event.target.checked);
      } else if (key === "EndorsedToNestingStatus") {
        setEndorsedToNesting(event.target.checked);
      }
      setStatusDispositionDataFiltered(trainee);
    } else {
      let oldStatusDispostionData = statusDispositionData.map((item) => {
        const initialData = globalData.find(
          (g) => g.OracleId === item.OracleId,
        );
        return {
          ...item,
          [key]: initialData ? initialData[key] : item[key],
        };
      });
      let value = statusDispositionDataFiltered.map((item) => {
        const initialData = globalData.find(
          (g) => g.OracleId === item.OracleId,
        );
        return {
          ...item,
          [key]: initialData ? initialData[key] : item[key],
        };
      });
      setStatusDispositionData(oldStatusDispostionData);
      dispatch(
        bulkEditStoreAction.setStatusDispositionsData(oldStatusDispostionData),
      );
      setStatusDispositionDataFiltered(value);
    }
  };

  const setProductionAllValues = (event, key) => {
    if (event.target.value === "" || event.target.value === " ") {
      let data1 = statusDispositionData.map((item) => {
        const initialData = globalData.find(
          (g) => g.OracleId === item.OracleId,
        );
        return {
          ...item,
          [key]: initialData ? initialData[key] : item[key],
        };
      });

      let updatedArray = statusDispositionDataFiltered.map((trainee) => {
        const data = globalData.find((g) => g.OracleId === trainee.OracleId);
        if (data) {
          return { ...trainee, [key]: data[key] };
        }
        return trainee;
      });

      setStatusDispositionData(data1);
      setStatusDispositionDataFiltered(updatedArray);
      dispatch(bulkEditStoreAction.setStatusDispositionsData(data1));
    } else {
      let data1 = statusDispositionData.map((item) => {
        return {
          ...item,
          [key]: event.target.value,
        };
      });
      let data = statusDispositionDataFiltered.map((item) => {
        return {
          ...item,
          [key]: event.target.value,
        };
      });
      setStatusDispositionData(data1);
      setStatusDispositionDataFiltered(data);
      dispatch(bulkEditStoreAction.setStatusDispositionsData(data1));
    }
  };

  const setTraineeStatusAllValues = (event) => {
    setTraineeStatusBottomDropdown(event.target.value);

    if (event.target.value === "" || event.target.value === " ") {
      let updatedStatusDispositionData = statusDispositionData.map((item) => {
        const initialData = globalData.find(
          (g) => g.OracleId === item.OracleId,
        );
        return {
          ...item,
          TraineeStatus: initialData
            ? initialData.TraineeStatus
            : item.TraineeStatus,
          TraineeStatusId: initialData
            ? initialData.TraineeStatusId
            : item.TraineeStatusId,
        };
      });
      let updatedStatusDispositionDataFiltered =
        statusDispositionDataFiltered.map((trainee) => {
          const data = globalData.find((g) => g.OracleId === trainee.OracleId);
          if (data) {
            return {
              ...trainee,
              TraineeStatus: data.TraineeStatus,
              TraineeStatusId: data.TraineeStatusId,
            };
          }
          return trainee;
        });
      setStatusDispositionData(updatedStatusDispositionData);
      setStatusDispositionDataFiltered(updatedStatusDispositionDataFiltered);
      dispatch(
        bulkEditStoreAction.setStatusDispositionsData(
          updatedStatusDispositionData,
        ),
      );
    } else {
      let data = statusDispositionData.map((item) => {
        return {
          ...item,
          TraineeStatus: event.target.value,
          TraineeStatusId: event.target.value,
        };
      });

      setStatusDispositionData(data);
      dispatch(bulkEditStoreAction.setStatusDispositionsData(data));

      let trainee = statusDispositionDataFiltered.map((item) => {
        return {
          ...item,
          TraineeStatus: event.target.value,
          TraineeStatusId: event.target.value,
        };
      });

      setStatusDispositionDataFiltered(trainee);
    }
  };

  const createData = () => {
    let values = statusDispositionDataFiltered.map((item) => {
      return {
        classTraineeId: item.ClassTraineeId,
        agentComments: item.AgentComments,
        ertStatus: item.ERTStatus,
        nestingStatus: item.NestingStatus,
        ortStatus: item.ORTStatus,
        productTrainingStatus: item.ProductTrainingStatus,
        productionStatus: item.ProductionStatus,
        tciDisposition: item.TCIDisposition,
        tciRecoveryDisposition: item.TCIRecoveryDisposition,
        ttfStatus: item.TTFStatus,
        techTestDisposition: item.TechTestDisposition,
        techTestRecoveryDisposition: item.TechTestRecoveryDisposition,
        traineeStatusId: item.TraineeStatusId.toString(),
        FinalTrainingRosterReadyTimestampStatus:
          getFTRosterReadyTimestampStatus(
            item.FinalTrainingRosterReadyTimestampStatus,
          ),
        EndorsedToNestingStatus: item.EndorsedToNestingStatus,
        overrideACC: JSON.stringify(item.OverrideACC || false),
        overrideTCI: JSON.stringify(item.OverrideTCI || false),
        overrideTechTest: JSON.stringify(item.OverrideTechTest || false),
        modifiedBy: localStorage.getItem("User"),
        traineeStatusReasonId:
          item.TraineeStatusReason === null ||
          item.TraineeStatusId.toString() !== "6"
            ? null
            : item.TraineeStatusReason.toString(),
      };
    });
    return values;
  };

  const saveStatusDisposition = async () => {
    setLoading(true);
    const values = createData();
    const response = await postStatusDispositionV2(values);
    if (response.message === "Success") {
      await fetchData();
      setSnack({
        open: true,
        message: "Saved Successfully",
        severity: "success",
      });
      resetAllStates();
      props.updateFilterStatusList();
    } else {
      setSnack({
        open: true,
        message: "Failed to Save",
        severity: "error",
      });
    }
    setLoading(false);
  };

  const navigateBack = () => {
    navigate(
      `/home/searchrosters?classRequestId=${classRequestId}&classRosterId=${classRosterId}`,
    );
  };

  const saveCloseStatusDisposition = async () => {
    setLoading(true);
    const values = createData();
    const response = await postStatusDispositionV2(values);
    if (response.message === "Success") {
      const obj = {
        open: true,
        message: "Saved Successfully !!",
        severity: "success",
      };
      dispatch(bulkEditStoreAction.setPreviousTabName(""));
      dispatch(SnackBarStoreAction.updateSnack(obj));
      navigateBack();
    } else {
      setSnack({
        open: true,
        message: "Failed to Save",
        severity: "error",
      });
    }
    setLoading(false);
  };

  const sorting = (col) => {
    let sorted = [];
    if (order === "ASC") {
      sorted = [...statusDispositionDataFiltered].sort((a, b) =>
        a[col]?.toLowerCase() > b[col]?.toLowerCase() ? 1 : -1,
      );
      setOrder("DSC");
    }
    if (order === "DSC") {
      sorted = [...statusDispositionDataFiltered].sort((a, b) =>
        a[col]?.toLowerCase() < b[col]?.toLowerCase() ? 1 : -1,
      );
      setOrder("ASC");
    }
    setStatusDispositionDataFiltered(sorted);
  };

  const applyFilter = (filterName, value, field) => {
    // Update the specific filter dropdown value state
    switch (filterName) {
      case "traineeStatus":
        // traineeStatusData is not used for filtering, so we don't set it
        break;
      case "ert":
        setErtDropDownValue(value);
        break;
      case "acc":
        setAccDropDownValue(value);
        break;
      case "tci":
        setTciDropDownValue(value);
        break;
      case "tciRecovery":
        setTciRecoveryDropDownValue(value);
        break;
      case "ttd":
        setTtdDropDownValue(value);
        break;
      case "ttdRecovery":
        setTtdRecoveryDropDownValue(value);
        break;
      case "ttf":
        setTtfDropDownValue(value);
        break;
      case "productTraining":
        setProductTrainingDropDownValue(value);
        break;
      case "nesting":
        setNestingDropDownValue(value);
        break;
      case "ort":
        setORTDropDownValue(value);
        break;
      case "ftrr":
        setFTRRDropDownValue(value);
        break;
      case "endorsedNesting":
        setEndorsedNestingDropDownValue(value);
        break;
      case "production":
        setProductionDropDownValue(value);
        break;
      default:
        break;
    }

    // Apply filter logic
    if (value !== "ALL") {
      let filterValue = value;

      // Special handling for ORT
      if (filterName === "ort") {
        if (value === "Pass") filterValue = "true";
        else if (value === "Fail") filterValue = "false";
        else if (value === null) filterValue = "null";
      }

      // Special handling for FTRR
      if (filterName === "ftrr") {
        filterValue = value === "True";
      }

      // Special handling for Endorsed to Nesting
      if (filterName === "endorsedNesting") {
        filterValue = value === "true" || value === true;
      }

      let data = statusDispositionData.filter(
        (item) => item[field] === filterValue,
      );
      setStatusDispositionDataFiltered(data);
    } else {
      setStatusDispositionDataFiltered(statusDispositionData);
    }

    handleFilterMenuClose(filterName);
  };

  const renderTextCell = (oracleId, name, traineeStatus, isInactive) => (
    <Grid item xs={0.8} align="center">
      <Typography
        sx={{
          fontFamily: "Urbanist",
          fontSize: "13px",
          color: "#3F5765",
          textDecoration: isInactive ? "line-through" : "none",
        }}
      >
        {name}({oracleId})
      </Typography>
      {isInactive && <p className={classes.blueColor}>{traineeStatus}</p>}
    </Grid>
  );

  const totalActiveCount = {
    total: statusDispositionDataFiltered.length,
    active: statusDispositionDataFiltered.filter(
      (row) => row.TraineeStatus === "Active",
    ).length,
  };

  return (
    <>
      {loading ? <BackdropComponent /> : null}
      <TableContainer
        className={classes.container}
        component={Paper}
        sx={{
          overflowX: "auto",
          overflowY: "auto",
        }}
      >
        <Table stickyHeader aria-label="sticky table" sx={{ minWidth: 2400 }}>
          <TableHead>
            <TableRow>
              {/* 1. FROZEN - Name/Oracle Id (NO BULK EDIT) */}
              <TableCell
                className={`${classes.tableHeaderCell} text-center align-center`}
                align="center"
                sx={{
                  minWidth: "320px !important",
                  maxWidth: 320,
                  position: "sticky",
                  left: 0,
                  top: "0 !important",
                  zIndex: "1100 !important",
                  boxShadow: "2px 0 5px -2px rgba(0,0,0,0.15)",
                  cursor: "pointer",
                  backgroundColor: "#e5e4e2 !important", // ADD THIS LINE
                }}
                onClick={() => sorting("Name")}
              >
                <Box sx={{ pt: 0.5 }}>
                  <Typography
                    sx={{
                      fontFamily: "Urbanist",
                      fontSize: "14px",
                      fontWeight: "bold",
                      mb: 0.5,
                    }}
                  >
                    Name/Oracle Id{" "}
                    <ArrowUpwardIcon
                      sx={{
                        fontSize: 16,
                        color: order === "ASC" ? "#1976d2" : "#9e9e9e",
                        verticalAlign: "middle",
                      }}
                    />
                    <ArrowDownwardIcon
                      sx={{
                        fontSize: 16,
                        color: order === "DSC" ? "#1976d2" : "#9e9e9e",
                        verticalAlign: "middle",
                      }}
                    />
                  </Typography>
                  <Typography
                    className={classes.blueColor}
                    sx={{ fontSize: "14px", margin: "0" }}
                  >
                    Total: {totalActiveCount.total} | Active:{" "}
                    {totalActiveCount.active}
                  </Typography>
                </Box>
              </TableCell>

              {/* 2. Training/Oracle Status/Reason - Filter Icon + Bulk Edit */}
              <TableCell
                className={`${classes.tableHeaderCell} text-center align-center`}
                sx={{ backgroundColor: "#e5e4e2 !important" }}
              >
                <Box sx={{ pt: 0.5 }}>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      mb: 0.5,
                    }}
                  >
                    <Typography
                      sx={{
                        fontFamily: "Urbanist",
                        fontSize: "14px",
                        fontWeight: "bold",
                      }}
                    >
                      Training/Oracle Status/Reason
                    </Typography>
                    <IconButton
                      size="small"
                      onClick={(e) => handleFilterMenuOpen(e, "traineeStatus")}
                      sx={{ ml: 0.5 }}
                    >
                      <FilterListIcon
                        sx={{
                          fontSize: 18,
                          color:
                            filterMenuAnchors.traineeStatus ||
                            statusDispositionDataFiltered.length !==
                              statusDispositionData.length
                              ? "#1976d2"
                              : "#3F5765",
                        }}
                      />
                    </IconButton>
                  </Box>
                  <FormControl className={classes.Select} size="small">
                    <Select
                      value={traineeStatusBottomDropdown}
                      onChange={setTraineeStatusAllValues}
                      displayEmpty
                      sx={{
                        fontFamily: "Urbanist",
                        fontSize: "12px",
                        bgcolor: "#fff",
                      }}
                      MenuProps={{
                        PaperProps: {
                          sx: {
                            "& .MuiMenuItem-root": { fontFamily: "Urbanist" },
                            "& .MuiMenuItem-root.Mui-selected": {
                              backgroundColor: "rgba(101, 128, 187, 1)",
                            },
                            "& .MuiMenuItem-root:hover": {
                              backgroundColor: "rgba(209, 217, 234, 1)",
                            },
                          },
                        },
                      }}
                    >
                      <MenuItem value=""></MenuItem>
                      {traineeStatusList().map((item) => (
                        <MenuItem key={item.id} value={item.id}>
                          <b>{item.traineeStatus}</b>
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Box>
              </TableCell>

              {/* Filter Menu for Training Status */}
              <Menu
                anchorEl={filterMenuAnchors.traineeStatus}
                open={Boolean(filterMenuAnchors.traineeStatus)}
                onClose={() => handleFilterMenuClose("traineeStatus")}
                MenuProps={{
                  PaperProps: {
                    sx: {
                      "& .MuiMenuItem-root": { fontFamily: "Urbanist" },
                      "& .MuiMenuItem-root.Mui-selected": {
                        backgroundColor: "rgba(101, 128, 187, 1)",
                      },
                      "& .MuiMenuItem-root:hover": {
                        backgroundColor: "rgba(209, 217, 234, 1)",
                      },
                    },
                  },
                }}
              >
                {traineeStatusDropDown.map((item) => (
                  <MenuItem
                    key={item}
                    onClick={() =>
                      applyFilter("traineeStatus", item, "TraineeStatus")
                    }
                  >
                    <b>{item}</b>
                  </MenuItem>
                ))}
              </Menu>

              {/* 3-8: Filter-only columns with Filter Icon */}
              {[
                {
                  filterName: "ert",
                  field: "ERTStatus",
                  label: "ERT/ACC",
                  values: ertValues,
                  activeValue: ertDropDownValue,
                },
                {
                  filterName: "acc",
                  field: "ACCStatus",
                  label: "ACC",
                  values: accValues,
                  activeValue: accDropDownValue,
                },
                {
                  filterName: "tci",
                  field: "TCIDisposition",
                  label: "TCI",
                  values: tciValues,
                  activeValue: tciDropDownValue,
                },
                {
                  filterName: "tciRecovery",
                  field: "TCIRecoveryDisposition",
                  label: "TCI Recovery",
                  values: tciRecoveryValues,
                  activeValue: tciRecoveryDropDownValue,
                },
                {
                  filterName: "ttd",
                  field: "TechTestDisposition",
                  label: "TTD/BOT",
                  values: ttdValues,
                  activeValue: ttdDropDownValue,
                },
                {
                  filterName: "ttdRecovery",
                  field: "TechTestRecoveryDisposition",
                  label: "TTD Recovery",
                  values: ttdRecoveryValues,
                  activeValue: ttdRecoveryDropDownValue,
                },
              ].map((col) => (
                <React.Fragment key={col.filterName}>
                  <TableCell
                    className={`${classes.tableHeaderCell} text-center align-center`}
                    sx={{ backgroundColor: "#e5e4e2 !important" }}
                  >
                    <Box sx={{ pt: 0.5 }}>
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                        }}
                      >
                        <Typography
                          sx={{
                            fontFamily: "Urbanist",
                            fontSize: "14px",
                            fontWeight: "bold",
                          }}
                        >
                          {col.label}
                        </Typography>
                        <IconButton
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleFilterMenuOpen(e, col.filterName);
                          }}
                          sx={{ ml: 0.5 }}
                        >
                          <FilterListIcon
                            sx={{
                              fontSize: 18,
                              color:
                                col.activeValue !== "ALL"
                                  ? "#1976d2"
                                  : "#3F5765",
                            }}
                          />
                        </IconButton>
                      </Box>
                    </Box>
                  </TableCell>

                  {/* Filter Menu */}
                  <Menu
                    anchorEl={filterMenuAnchors[col.filterName]}
                    open={Boolean(filterMenuAnchors[col.filterName])}
                    onClose={() => handleFilterMenuClose(col.filterName)}
                    MenuProps={{
                      PaperProps: {
                        sx: {
                          "& .MuiMenuItem-root": { fontFamily: "Urbanist" },
                          "& .MuiMenuItem-root.Mui-selected": {
                            backgroundColor: "rgba(101, 128, 187, 1)",
                          },
                          "& .MuiMenuItem-root:hover": {
                            backgroundColor: "rgba(209, 217, 234, 1)",
                          },
                        },
                      },
                    }}
                  >
                    {col.values.map((item) => (
                      <MenuItem
                        key={item}
                        onClick={() =>
                          applyFilter(col.filterName, item, col.field)
                        }
                      >
                        <b>{item}</b>
                      </MenuItem>
                    ))}
                  </Menu>
                </React.Fragment>
              ))}

              {/* 9-11: TTF, Product Training, Nesting - Filter Icon + Bulk Edit */}
              {[
                {
                  filterName: "ttf",
                  values: ttfValues,
                  label: "TTF",
                  bottomValue: ttf,
                  setBottomValue: setTtf,
                  field: "TTFStatus",
                  activeValue: ttfDropDownValue,
                },
                {
                  filterName: "productTraining",
                  values: productTrainingValues,
                  label: "Production Training",
                  bottomValue: productTraining,
                  setBottomValue: setProductTraining,
                  field: "ProductTrainingStatus",
                  activeValue: productTrainingDropDownValue,
                },
                {
                  filterName: "nesting",
                  values: nestingValues,
                  label: "Nesting",
                  bottomValue: nesting,
                  setBottomValue: setNesting,
                  field: "NestingStatus",
                  activeValue: nestingDropDownValue,
                },
              ].map((col) => (
                <React.Fragment key={col.filterName}>
                  <TableCell
                    className={`${classes.tableHeaderCell} text-center align-center`}
                    sx={{ backgroundColor: "#e5e4e2 !important" }}
                  >
                    <Box sx={{ pt: 0.5 }}>
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          mb: 0.5,
                        }}
                      >
                        <Typography
                          sx={{
                            fontFamily: "Urbanist",
                            fontSize: "14px",
                            fontWeight: "bold",
                          }}
                        >
                          {col.label}
                        </Typography>
                        <IconButton
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleFilterMenuOpen(e, col.filterName);
                          }}
                          sx={{ ml: 0.5 }}
                        >
                          <FilterListIcon
                            sx={{
                              fontSize: 18,
                              color:
                                col.activeValue !== "ALL"
                                  ? "#1976d2"
                                  : "#3F5765",
                            }}
                          />
                        </IconButton>
                      </Box>
                      <FormControl className={classes.Select} size="small">
                        <Select
                          value={col.bottomValue}
                          onChange={(e) => {
                            col.setBottomValue(e.target.value);
                            setProductionAllValues(e, col.field);
                          }}
                          displayEmpty
                          sx={{
                            fontFamily: "Urbanist",
                            fontSize: "12px",
                            bgcolor: "#fff",
                          }}
                          MenuProps={{
                            PaperProps: {
                              sx: {
                                "& .MuiMenuItem-root": {
                                  fontFamily: "Urbanist",
                                },
                                "& .MuiMenuItem-root.Mui-selected": {
                                  backgroundColor: "rgba(101, 128, 187, 1)",
                                },
                                "& .MuiMenuItem-root:hover": {
                                  backgroundColor: "rgba(209, 217, 234, 1)",
                                },
                              },
                            },
                          }}
                        >
                          <MenuItem value=""></MenuItem>
                          {TTFDropDown().map((item) => (
                            <MenuItem key={item} value={item}>
                              <b>{item}</b>
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Box>
                  </TableCell>

                  {/* Filter Menu */}
                  <Menu
                    anchorEl={filterMenuAnchors[col.filterName]}
                    open={Boolean(filterMenuAnchors[col.filterName])}
                    onClose={() => handleFilterMenuClose(col.filterName)}
                    MenuProps={{
                      PaperProps: {
                        sx: {
                          "& .MuiMenuItem-root": { fontFamily: "Urbanist" },
                          "& .MuiMenuItem-root.Mui-selected": {
                            backgroundColor: "rgba(101, 128, 187, 1)",
                          },
                          "& .MuiMenuItem-root:hover": {
                            backgroundColor: "rgba(209, 217, 234, 1)",
                          },
                        },
                      },
                    }}
                  >
                    {col.values.map((item) => (
                      <MenuItem
                        key={item}
                        onClick={() =>
                          applyFilter(col.filterName, item, col.field)
                        }
                      >
                        <b>{item}</b>
                      </MenuItem>
                    ))}
                  </Menu>
                </React.Fragment>
              ))}

              {/* 12. ORT - Filter Icon */}
              <TableCell
                className={`${classes.tableHeaderCell} text-center align-center`}
                sx={{ backgroundColor: "#e5e4e2 !important" }}
              >
                <Box sx={{ pt: 0.5 }}>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <Typography
                      sx={{
                        fontFamily: "Urbanist",
                        fontSize: "14px",
                        fontWeight: "bold",
                      }}
                    >
                      ORT
                    </Typography>
                    <IconButton
                      size="small"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleFilterMenuOpen(e, "ort");
                      }}
                      sx={{ ml: 0.5 }}
                    >
                      <FilterListIcon
                        sx={{
                          fontSize: 18,
                          color:
                            ortDropDownValue !== "ALL" ? "#1976d2" : "#3F5765",
                        }}
                      />
                    </IconButton>
                  </Box>
                </Box>
              </TableCell>

              {/* ORT Filter Menu */}
              <Menu
                anchorEl={filterMenuAnchors.ort}
                open={Boolean(filterMenuAnchors.ort)}
                onClose={() => handleFilterMenuClose("ort")}
                MenuProps={{
                  PaperProps: {
                    sx: {
                      "& .MuiMenuItem-root": { fontFamily: "Urbanist" },
                      "& .MuiMenuItem-root.Mui-selected": {
                        backgroundColor: "rgba(101, 128, 187, 1)",
                      },
                      "& .MuiMenuItem-root:hover": {
                        backgroundColor: "rgba(209, 217, 234, 1)",
                      },
                    },
                  },
                }}
              >
                {ortValues.map((item) => (
                  <MenuItem
                    key={item}
                    onClick={() => applyFilter("ort", item, "ORTStatus")}
                  >
                    <b>{item}</b>
                  </MenuItem>
                ))}
              </Menu>

              {/* 13-14: FTRR, Endorsed to Nesting - Filter Icon + Bulk Edit */}
              {[
                {
                  filterName: "ftrr",
                  values: ftrrValues,
                  label: "FTRR",
                  checked: ftrr,
                  field: "FinalTrainingRosterReadyTimestampStatus",
                  activeValue: ftrrDropDownValue,
                },
                {
                  filterName: "endorsedNesting",
                  values: endorsedNestingValues,
                  label: "Endorsed to Nesting",
                  checked: endorsedToNesting,
                  field: "EndorsedToNestingStatus",
                  activeValue: endorsedNestingDropDownValue,
                },
              ].map((col) => (
                <React.Fragment key={col.filterName}>
                  <TableCell
                    className={`${classes.tableHeaderCell} text-center align-center`}
                    sx={{ backgroundColor: "#e5e4e2 !important" }}
                  >
                    <Box sx={{ pt: 0.5 }}>
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          mb: 0.5,
                        }}
                      >
                        <Typography
                          sx={{
                            fontFamily: "Urbanist",
                            fontSize: "14px",
                            fontWeight: "bold",
                          }}
                        >
                          {col.label}
                        </Typography>
                        <IconButton
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleFilterMenuOpen(e, col.filterName);
                          }}
                          sx={{ ml: 0.5 }}
                        >
                          <FilterListIcon
                            sx={{
                              fontSize: 18,
                              color:
                                col.activeValue !== "ALL"
                                  ? "#1976d2"
                                  : "#3F5765",
                            }}
                          />
                        </IconButton>
                      </Box>
                      <Box sx={{ textAlign: "center" }}>
                        <Checkbox
                          sx={{ color: "#3F5765", padding: 0 }}
                          checked={col.checked}
                          onChange={(e) => {
                            if (
                              col.field ===
                              "FinalTrainingRosterReadyTimestampStatus"
                            ) {
                              setFtrr(e.target.checked);
                            } else {
                              setEndorsedToNesting(e.target.checked);
                            }
                            handleCheckChangeAll(e, col.field);
                          }}
                          name={col.field}
                        />
                      </Box>
                    </Box>
                  </TableCell>

                  {/* Filter Menu */}
                  <Menu
                    anchorEl={filterMenuAnchors[col.filterName]}
                    open={Boolean(filterMenuAnchors[col.filterName])}
                    onClose={() => handleFilterMenuClose(col.filterName)}
                    MenuProps={{
                      PaperProps: {
                        sx: {
                          "& .MuiMenuItem-root": { fontFamily: "Urbanist" },
                          "& .MuiMenuItem-root.Mui-selected": {
                            backgroundColor: "rgba(101, 128, 187, 1)",
                          },
                          "& .MuiMenuItem-root:hover": {
                            backgroundColor: "rgba(209, 217, 234, 1)",
                          },
                        },
                      },
                    }}
                  >
                    {col.values.map((item) => (
                      <MenuItem
                        key={item}
                        onClick={() =>
                          applyFilter(col.filterName, item, col.field)
                        }
                      >
                        <b>{item}</b>
                      </MenuItem>
                    ))}
                  </Menu>
                </React.Fragment>
              ))}

              {/* 15. Endorsed to Production - Filter Icon + Bulk Edit */}
              <TableCell
                className={`${classes.tableHeaderCell} text-center align-center`}
                sx={{ backgroundColor: "#e5e4e2 !important" }}
              >
                <Box sx={{ pt: 0.5 }}>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      mb: 0.5,
                    }}
                  >
                    <Typography
                      sx={{
                        fontFamily: "Urbanist",
                        fontSize: "14px",
                        fontWeight: "bold",
                      }}
                    >
                      Endorsed to Production
                    </Typography>
                    <IconButton
                      size="small"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleFilterMenuOpen(e, "production");
                      }}
                      sx={{ ml: 0.5 }}
                    >
                      <FilterListIcon
                        sx={{
                          fontSize: 18,
                          color:
                            productionDropDownValue !== "ALL"
                              ? "#1976d2"
                              : "#3F5765",
                        }}
                      />
                    </IconButton>
                  </Box>
                  <FormControl className={classes.Select} size="small">
                    <Select
                      value={initialProductionValue}
                      onChange={(e) => {
                        setInitialProductionValue(e.target.value);
                        setProductionAllValues(e, "ProductionStatus");
                      }}
                      displayEmpty
                      sx={{
                        fontFamily: "Urbanist",
                        fontSize: "12px",
                        bgcolor: "#fff",
                      }}
                      MenuProps={{
                        PaperProps: {
                          sx: {
                            "& .MuiMenuItem-root": { fontFamily: "Urbanist" },
                            "& .MuiMenuItem-root.Mui-selected": {
                              backgroundColor: "rgba(101, 128, 187, 1)",
                            },
                            "& .MuiMenuItem-root:hover": {
                              backgroundColor: "rgba(209, 217, 234, 1)",
                            },
                          },
                        },
                      }}
                    >
                      <MenuItem value=""></MenuItem>
                      {productionDropDown().map((item) => (
                        <MenuItem key={item} value={item}>
                          <b>{item}</b>
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Box>
              </TableCell>

              {/* Production Filter Menu */}
              <Menu
                anchorEl={filterMenuAnchors.production}
                open={Boolean(filterMenuAnchors.production)}
                onClose={() => handleFilterMenuClose("production")}
                MenuProps={{
                  PaperProps: {
                    sx: {
                      "& .MuiMenuItem-root": { fontFamily: "Urbanist" },
                      "& .MuiMenuItem-root.Mui-selected": {
                        backgroundColor: "rgba(101, 128, 187, 1)",
                      },
                      "& .MuiMenuItem-root:hover": {
                        backgroundColor: "rgba(209, 217, 234, 1)",
                      },
                    },
                  },
                }}
              >
                {productionValues.map((item) => (
                  <MenuItem
                    key={item}
                    onClick={() =>
                      applyFilter("production", item, "ProductionStatus")
                    }
                  >
                    <b>{item}</b>
                  </MenuItem>
                ))}
              </Menu>

              {/* 16-18: Override Columns - No Filter */}
              {["ACC (Override)", "TCI (Override)", "TTD/BOT"].map((label) => (
                <TableCell
                  key={label}
                  className={`${classes.tableHeaderCell} text-center align-center`}
                  sx={{ backgroundColor: "#e5e4e2 !important" }}
                >
                  <Box sx={{ pt: 0.5 }}>
                    <Typography
                      sx={{
                        fontFamily: "Urbanist",
                        fontSize: "14px",
                        fontWeight: "bold",
                        mb: 0.5,
                      }}
                    >
                      {label}
                    </Typography>
                    <Typography
                      sx={{
                        fontFamily: "Urbanist",
                        fontSize: "11px",
                        color: "#3F5765",
                      }}
                    >
                      Set to: Pass
                    </Typography>
                  </Box>
                </TableCell>
              ))}
            </TableRow>
          </TableHead>

          <TableBody>
            {statusDispositionDataFiltered.length > 0 ? (
              statusDispositionDataFiltered.map((row, i) => {
                const rowKey = `${row.OracleId}-${i}`;
                return (
                  <TableRow key={rowKey} hover>
                    {/* 1. FROZEN - Employee + Comments */}
                    <TableCell
                      align="center"
                      sx={{
                        position: "sticky",
                        left: 0,
                        backgroundColor: "#fff",
                        zIndex: 1000,
                        boxShadow: "2px 0 5px -2px rgba(0,0,0,0.15)",
                        padding: "12px 8px",
                      }}
                    >
                      <Grid
                        container
                        direction="row"
                        justifyContent="center"
                        columns={1}
                        alignItems="center"
                        spacing={0}
                      >
                        {renderTextCell(
                          row.OracleId,
                          row.Name,
                          row.TraineeStatus,
                          row.TraineeStatus !== "Active",
                        )}
                        {row.TTECRegistrationStatus ? (
                          <Grid item xs={0.1}>
                            <Tooltip title="TTEConnect: Registered">
                              <PowerSharpIcon
                                sx={{ color: "#69DE00", fontSize: 18 }}
                              />
                            </Tooltip>
                          </Grid>
                        ) : (
                          <Grid item xs={0.1}></Grid>
                        )}
                      </Grid>
                      <Box sx={{ mt: 2, width: "100%" }}>
                        <LastUpdateTooltip
                          AgentCommentsModifiedDate={
                            row.AgentCommentsModifiedDate
                          }
                          AgentCommentsModifiedBy={row.AgentCommentsModifiedBy}
                        >
                          <textarea
                            className={classes.agentCommentInputContainer}
                            value={row.AgentComments || ""}
                            maxLength={4000}
                            placeholder="Max 4000 characters"
                            onChange={(e) =>
                              handleChange(e, "AgentComments", i, row)
                            }
                          />
                        </LastUpdateTooltip>
                        <Box
                          sx={{
                            display: "flex",
                            justifyContent: "flex-end",
                            mt: 0.5,
                            px: 0.5,
                          }}
                        >
                          <Typography
                            sx={{
                              fontFamily: "Urbanist",
                              fontSize: "10px",
                              color: "#666",
                            }}
                          >
                            {(row.AgentComments || "").length}/4000
                          </Typography>
                        </Box>
                      </Box>
                    </TableCell>

                    {/* 2. Trainee Status + Reason */}
                    <TableCell align="center" sx={{ padding: "8px" }}>
                      <FormControl className={classes.Select} size="small">
                        <Select
                          value={row.TraineeStatusId}
                          onChange={(e) =>
                            handleChange(e, "TraineeStatusId", i, row)
                          }
                          sx={{ fontFamily: "Urbanist", fontSize: "12px" }}
                          MenuProps={{
                            PaperProps: {
                              sx: {
                                "& .MuiMenuItem-root": {
                                  fontFamily: "Urbanist",
                                },
                                "& .MuiMenuItem-root.Mui-selected": {
                                  backgroundColor: "rgba(101, 128, 187, 1)",
                                },
                                "& .MuiMenuItem-root:hover": {
                                  backgroundColor: "rgba(209, 217, 234, 1)",
                                },
                              },
                            },
                          }}
                        >
                          {traineeStatusList().map((item) => (
                            <MenuItem key={item.id} value={item.id}>
                              <b>{item.traineeStatus}</b>
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                      {row.TraineeStatusId === 6 && (
                        <FormControl
                          className={classes.Select}
                          size="small"
                          sx={{ mt: 1 }}
                        >
                          <Select
                            value={row.TraineeStatusReason}
                            onChange={(e) =>
                              handleChange(e, "TraineeStatusReason", i, row)
                            }
                            sx={{ fontFamily: "Urbanist", fontSize: "12px" }}
                            MenuProps={{
                              PaperProps: {
                                sx: {
                                  "& .MuiMenuItem-root": {
                                    fontFamily: "Urbanist",
                                  },
                                  "& .MuiMenuItem-root.Mui-selected": {
                                    backgroundColor: "rgba(101, 128, 187, 1)",
                                  },
                                  "& .MuiMenuItem-root:hover": {
                                    backgroundColor: "rgba(209, 217, 234, 1)",
                                  },
                                },
                              },
                            }}
                          >
                            {reasonData.map((item) => (
                              <MenuItem
                                key={item.TraineeStatusReasonId}
                                value={item.TraineeStatusReasonId}
                              >
                                <b>{item.TraineeStatusReason}</b>
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      )}
                      <Typography
                        sx={{
                          fontFamily: "Urbanist",
                          fontSize: "11px",
                          color: "#3F5765",
                          mt: 0.5,
                        }}
                      >
                        {row.EmployeeStatus}
                      </Typography>
                    </TableCell>

                    {/* 3. ERT/ACC */}
                    <TableCell align="center" sx={{ padding: "8px" }}>
                      <RadioGroup
                        value={row.ERTStatus}
                        name="ertStatus"
                        onChange={(e) => handleChange(e, "ERTStatus", i, row)}
                      >
                        {["Pass", "Fail", "Pending", "DidNotAttempt"].map(
                          (val) => (
                            <FormControlLabel
                              key={val}
                              value={val}
                              control={<Radio size="small" />}
                              label={
                                <Typography
                                  sx={{
                                    fontFamily: "Urbanist",
                                    fontSize: "12px",
                                    whiteSpace: "nowrap", // prevents "Did not attempt" from wrapping
                                  }}
                                >
                                  {val === "DidNotAttempt"
                                    ? "Did not attempt"
                                    : val}
                                </Typography>
                              }
                            />
                          ),
                        )}
                      </RadioGroup>
                    </TableCell>

                    {/* 4-6: ACC, TCI, TCI Recovery */}
                    <TableCell align="center" sx={{ padding: "8px" }}>
                      <Typography
                        sx={{
                          fontFamily: "Urbanist",
                          fontSize: "12px",
                          color: "#3F5765",
                        }}
                        className={
                          row.OverrideACC
                            ? "text-decoration-line-bulk-default"
                            : ""
                        }
                      >
                        {row.ACCStatus}
                      </Typography>
                      {row.OverrideACC && (
                        <Typography
                          className={classes.blueColor}
                          sx={{
                            fontFamily: "Urbanist",
                            fontSize: "12px",
                            fontWeight: "bold",
                            mt: 0.5,
                          }}
                        >
                          Pass (Override)
                        </Typography>
                      )}
                    </TableCell>

                    <TableCell align="center" sx={{ padding: "8px" }}>
                      <Typography
                        sx={{
                          fontFamily: "Urbanist",
                          fontSize: "12px",
                          color: "#3F5765",
                        }}
                        className={
                          row.OverrideTCI
                            ? "text-decoration-line-bulk-default"
                            : ""
                        }
                      >
                        {row.TCIDisposition}
                      </Typography>
                      {row.OverrideTCI && (
                        <Typography
                          className={classes.blueColor}
                          sx={{
                            fontFamily: "Urbanist",
                            fontSize: "12px",
                            fontWeight: "bold",
                            mt: 0.5,
                          }}
                        >
                          Pass (Override)
                        </Typography>
                      )}
                    </TableCell>

                    <TableCell align="center" sx={{ padding: "8px" }}>
                      <Typography
                        sx={{
                          fontFamily: "Urbanist",
                          fontSize: "12px",
                          color: "#3F5765",
                        }}
                      >
                        {row.TCIRecoveryDisposition}
                      </Typography>
                    </TableCell>

                    {/* 7-8: TTD, TTD Recovery */}
                    <TableCell align="center" sx={{ padding: "8px" }}>
                      <Typography
                        sx={{
                          fontFamily: "Urbanist",
                          fontSize: "12px",
                          color: "#3F5765",
                        }}
                        className={
                          row.OverrideTechTest
                            ? "text-decoration-line-bulk-default"
                            : ""
                        }
                      >
                        {row.TechTestDisposition}
                      </Typography>
                      {row.OverrideTechTest && (
                        <Typography
                          className={classes.blueColor}
                          sx={{
                            fontFamily: "Urbanist",
                            fontSize: "12px",
                            fontWeight: "bold",
                            mt: 0.5,
                          }}
                        >
                          Pass (Override)
                        </Typography>
                      )}
                    </TableCell>

                    <TableCell align="center" sx={{ padding: "8px" }}>
                      <Typography
                        sx={{
                          fontFamily: "Urbanist",
                          fontSize: "12px",
                          color: "#3F5765",
                        }}
                      >
                        {row.TechTestRecoveryDisposition}
                      </Typography>
                    </TableCell>

                    {/* 9-11: TTF, Product Training, Nesting */}
                    {[
                      "TTFStatus",
                      "ProductTrainingStatus",
                      "NestingStatus",
                    ].map((field) => (
                      <TableCell
                        key={field}
                        align="center"
                        sx={{ padding: "8px" }}
                      >
                        <FormControl className={classes.Select} size="small">
                          <Select
                            value={row[field]}
                            onChange={(e) => handleChange(e, field, i, row)}
                            sx={{ fontFamily: "Urbanist", fontSize: "12px" }}
                            MenuProps={{
                              PaperProps: {
                                sx: {
                                  "& .MuiMenuItem-root": {
                                    fontFamily: "Urbanist",
                                  },
                                  "& .MuiMenuItem-root.Mui-selected": {
                                    backgroundColor: "rgba(101, 128, 187, 1)",
                                  },
                                  "& .MuiMenuItem-root:hover": {
                                    backgroundColor: "rgba(209, 217, 234, 1)",
                                  },
                                },
                              },
                            }}
                          >
                            {TTFDropDown().map((item) => (
                              <MenuItem key={item} value={item}>
                                <b>{item}</b>
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                        <Typography
                          sx={{
                            fontFamily: "Urbanist",
                            fontSize: "11px",
                            color: "#3F5765",
                            mt: 0.5,
                          }}
                        >
                          {row[field]}
                        </Typography>
                      </TableCell>
                    ))}

                    {/* 12. ORT */}
                    <TableCell align="center" sx={{ padding: "8px" }}>
                      <RadioGroup
                        value={row.ORTStatus}
                        name="ortStatus"
                        onChange={(e) => handleChange(e, "ORTStatus", i, row)}
                      >
                        {[
                          { val: "true", label: "Pass" },
                          { val: "false", label: "Fail" },
                          { val: "null", label: "N/A" },
                        ].map((opt) => (
                          <FormControlLabel
                            key={opt.val}
                            value={opt.val}
                            control={<Radio size="small" />}
                            label={
                              <Typography
                                sx={{
                                  fontFamily: "Urbanist",
                                  fontSize: "12px",
                                }}
                              >
                                {opt.label}
                              </Typography>
                            }
                          />
                        ))}
                      </RadioGroup>
                    </TableCell>

                    {/* 13-14: FTRR, Endorsed to Nesting */}
                    {[
                      "FinalTrainingRosterReadyTimestampStatus",
                      "EndorsedToNestingStatus",
                    ].map((field) => (
                      <TableCell
                        key={field}
                        align="center"
                        sx={{ padding: "8px" }}
                      >
                        <Checkbox
                          sx={{ color: "#3F5765" }}
                          name={field}
                          checked={row[field]}
                          onChange={(e) => handleChangeChecked(e, row, i)}
                        />
                      </TableCell>
                    ))}

                    {/* 15. Endorsed to Production */}
                    <TableCell align="center" sx={{ padding: "8px" }}>
                      <FormControl className={classes.Select} size="small">
                        <Select
                          value={row.ProductionStatus || "No - Unknown"}
                          onChange={(e) =>
                            handleChange(e, "ProductionStatus", i, row)
                          }
                          sx={{ fontFamily: "Urbanist", fontSize: "12px" }}
                          MenuProps={{
                            PaperProps: {
                              sx: {
                                "& .MuiMenuItem-root": {
                                  fontFamily: "Urbanist",
                                },
                                "& .MuiMenuItem-root.Mui-selected": {
                                  backgroundColor: "rgba(101, 128, 187, 1)",
                                },
                                "& .MuiMenuItem-root:hover": {
                                  backgroundColor: "rgba(209, 217, 234, 1)",
                                },
                              },
                            },
                          }}
                        >
                          {productionDropDown().map((item) => (
                            <MenuItem key={item} value={item}>
                              <b>{item}</b>
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                      <Typography
                        sx={{
                          fontFamily: "Urbanist",
                          fontSize: "11px",
                          color: "#3F5765",
                          mt: 0.5,
                        }}
                      >
                        {row.ProductionStatus || "No - Unknown"}
                      </Typography>
                    </TableCell>

                    {/* 16-18: Override Checkboxes */}
                    {["OverrideACC", "OverrideTCI", "OverrideTechTest"].map(
                      (field) => (
                        <TableCell
                          key={field}
                          align="center"
                          sx={{ padding: "8px" }}
                        >
                          <Checkbox
                            sx={{ color: "#3F5765" }}
                            checked={row[field] || false}
                            onChange={(e) =>
                              handleChangeChecked(
                                {
                                  target: {
                                    name: field,
                                    checked: e.target.checked,
                                  },
                                },
                                row,
                                i,
                              )
                            }
                          />
                        </TableCell>
                      ),
                    )}
                  </TableRow>
                );
              })
            ) : (
              <TableRow>
                <TableCell
                  colSpan={18}
                  align="center"
                  sx={{ fontFamily: "Urbanist", padding: "20px" }}
                >
                  <Typography
                    sx={{
                      fontFamily: "Urbanist",
                      fontSize: "14px",
                      color: "#3F5765",
                    }}
                  >
                    No data available
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <SnackBar snack={snack} snackReset={snackReset}></SnackBar>

      <Grid
        item
        xs={12}
        sx={{
          display: "flex",
          justifyContent: "flex-end",
          alignItems: "center",
          paddingTop: 1,
          gap: 1,
        }}
      >
        <CustomOutlinedButton variant="outlined" onClick={navigateBack}>
          {t("CANCEL")}
        </CustomOutlinedButton>
        <CustomButton onClick={() => saveStatusDisposition()}>
          {t("SAVE")}
        </CustomButton>
        <CustomButton onClick={() => saveCloseStatusDisposition()}>
          {t("SAVE+CLOSE")}
        </CustomButton>
      </Grid>
    </>
  );
};

StatusDisposition.propTypes = {
  filterStatus: PropTypes.string,
  closeLoader: PropTypes.func,
  resetStatus: PropTypes.bool,
  onChange: PropTypes.func,
  updateFilterStatusList: PropTypes.func,
};

export default StatusDisposition;
